<template>
    <div class="relative">
        <div class="mb-6 flex items-center justify-between">
            <div class="space-y-1">
                <ULink to="/calls" class="flex items-center space-x-1 text-sm font-semibold text-gray-500 hover:text-gray-700">
                    <span>Calls</span>
                    <UIcon name="i-ph-caret-right-bold" class="size-2.5" />
                </ULink>
                <div v-if="call" class="flex items-center space-x-4">
                    <h1 class="text-3xl font-semibold text-gray-700">Call #{{ call.number }}</h1>
                    <BadgeCallStatus :status="call.status" />
                </div>
            </div>

            <div class="flex items-center gap-x-2">
                <UButton
                    @click="overlays.createFeedbackReport = true" 
                    label="Feedback Report"
                    color="orange"
                    icon="i-ph-megaphone-bold"
                />
            </div>
        </div>

        <div v-if="call" class="relative flex items-start gap-4">
            <div class="sticky top-0 flex-shrink-0 w-96 space-y-4">
                <div class="relative bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                    <div class="flex items-center px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Details</h3>
                    </div>
                    <div class="px-6 py-5 space-y-4">
                        <div class="grid grid-cols-2 gap-4">
                            <Field label="ID">
                                <span class="font-mono">{{ callId }}</span>
                            </Field>
                            <Field label="Source">
                                <BadgeCallSource :source="call.source" />
                            </Field>
                        </div>
                        <Field label="Last Active User">
                            <div v-if="call.user" class="flex items-center space-x-2">
                                <span>{{ call.user.name }}</span>
                                <BadgeUserRole :role="call.user.role" />
                            </div>
                        </Field>
                        <div class="grid grid-cols-2 gap-4">
                            <Field label="Created">
                                <span>{{ dayjs(call.created_at).format('MMM D, YYYY HH:mm') }}</span>
                            </Field>
                            <Field label="Time to Answer (TTA)">
                                <span v-if="call.connected_at">{{ duration(call.created_at, call.connected_at) }}</span>
                            </Field>
                        </div>
                        <div class="grid grid-cols-2 gap-4">
                            <Field label="Connected">
                                <span v-if="call.connected_at">{{ dayjs(call.connected_at).format('MMM D, YYYY HH:mm') }}</span>
                            </Field>
                            <Field label="Call Duration">
                                <span v-if="call.ended_at">{{ duration(call.created_at, call.ended_at) }}</span>
                            </Field>
                        </div>
                        <Field label="Ended">
                            <span v-if="call.ended_at">{{ dayjs(call.ended_at).format('MMM D, YYYY HH:mm') }}</span>
                        </Field>
                        <Field v-if="(auth.hasRole(UserRole.admin) || auth.hasRole(UserRole.medicalDirector)) && call.metadata?.feedback" label="Feedback">
                            <div class="space-y-2">
                                <div class="flex items-center gap-1.5">
                                    <div class="flex">
                                        <UIcon
                                            v-for="star in 5"
                                            :key="star"
                                            :name="star <= call.metadata.feedback.rating ? 'i-ph-star-fill' : 'i-ph-star'"
                                            :class="star <= call.metadata.feedback.rating ? 'text-yellow-500' : 'text-gray-300'"
                                            class="size-4"
                                        />
                                    </div>
                                    <span class="text-sm text-gray-500">{{ call.metadata.feedback.rating }}/5</span>
                                </div>
                                <div v-if="call.metadata.feedback.comment" class="bg-gray-50 border border-gray-100 rounded-md px-3 py-2">
                                    <p class="text-sm text-gray-700">{{ call.metadata.feedback.comment }}</p>
                                    <p class="text-xs text-gray-500 text-right mt-1">{{ dayjs(call.metadata.feedback.timestamp).format('MMM D, YYYY HH:mm') }}</p>
                                </div>
                            </div>
                        </Field>
                    </div>
                </div>

                <div v-if="hasDifferentCaller && call.caller" class="relative bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                    <div class="flex items-center justify-between px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Caller</h3>
                        <ULink :to="`/customers/${call.caller.id}`" class="flex items-center space-x-1 text-xs font-medium hover:underline">
                            <span>View caller</span>
                            <UIcon name="i-ph-arrow-right-bold" />
                        </ULink>
                    </div>
                    <div class="px-6 py-5">
                        <div class="flex items-center space-x-4">
                            <Avatar
                                modal
                                size="lg"
                                class="flex-shrink-0"
                                :path="call.caller.avatar"
                                :placeholder="[call.caller.first_name, call.caller.last_name].filter(Boolean).join(' ')"
                            />
                            <div class="flex-1 flex flex-col overflow-hidden">
                                <h3 :title="callerName" class="leading-5 font-medium text-gray-800 truncate">{{ callerName }}</h3>
                                <span class="text-sm capitalize">{{ callerDemographics }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="relative bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                    <div class="flex items-center justify-between px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Customer</h3>
                        <ULink :to="`/customers/${call.customer.id}`" class="flex items-center space-x-1 text-xs font-medium hover:underline">
                            <span>View customer</span>
                            <UIcon name="i-ph-arrow-right-bold" />
                        </ULink>
                    </div>
                    <div class="px-6 py-5 space-y-4">
                        <div class="col-span-4 flex items-center space-x-4">
                            <Avatar
                                modal
                                size="lg"
                                class="flex-shrink-0"
                                :path="call.customer.avatar"
                                :placeholder="[call.customer.first_name, call.customer.last_name].filter(Boolean).join(' ')"
                            />
                            <div class="flex-1 flex flex-col overflow-hidden">
                                <h3 :title="customerName" class="leading-5 font-medium text-gray-800 truncate">{{ customerName }}</h3>
                                <span class="text-sm capitalize">{{ customerDemographics }}</span>
                            </div>
                        </div>
                        <Field label="Subscription">
                            <BadgeSubscriptionStatus with-expiry :subscription="activeSubscription" class="mt-1.5" />
                        </Field>
                        <Field v-if="activeSubscription?.partner" label="Partner">
                            <div class="flex items-center space-x-2.5 mt-1.5">
                                <Avatar bucket="media" folder="partners" size="sm" :placeholder="activeSubscription.partner.name" :path="activeSubscription.partner.avatar" />
                                <span>{{ activeSubscription.partner.name }}</span>
                            </div>
                        </Field>
                        <Field label="Policy Holder">
                            <span v-if="call.customer?.policy_holder">{{ call.customer.policy_holder }}</span>
                        </Field>
                        <Field label="Tags">
                            <div v-if="call.customer.tags?.length" class="flex flex-wrap gap-1 mt-1">
                                <UBadge v-for="tag in call.customer.tags" :key="tag" :label="tag" color="gray" />
                            </div>
                        </Field>
                        <div class="grid grid-cols-2 gap-4">
                            <Field label="Emergency Phone">
                                <PhoneLabel v-if="call.customer.metadata?.emergency_phone" :value="call.customer.metadata.emergency_phone" />
                            </Field>
                            <Field label="Emergency Contact" truncate>
                                <span v-if="call.customer.metadata?.emergency_contact">{{ call.customer.metadata.emergency_contact }}</span>
                            </Field>
                        </div>
                        <Field label="Registered Phone">
                            <PhoneLabel v-if="call.customer.phone" :value="call.customer.phone" />
                        </Field>
                        <Field v-if="call.customer.metadata?.alternate_phone" label="Alternate Phone">
                            <PhoneLabel :value="call.customer.metadata.alternate_phone" />
                        </Field>
                    </div>
                </div>
            </div>
            <div class="flex-1">
                <div class="flex flex-col bg-white ring-1 ring-black/5 rounded-lg shadow-sm">
                    <UTabs 
                        v-model="selectedTab" 
                        class="flex-shrink-0"
                        :items="tabs" 
                        :ui="tabsUi"
                    >
                        <template #default="{ item }">
                            <div class="flex items-center space-x-2">
                                <UIcon :name="item.icon" class="w-4 h-4 flex-shrink-0" />
                                <span>{{ item.label }}</span>
                            </div>
                        </template>
                    </UTabs>

                    <div class="flex-1 p-5">
                        <template v-if="selectedTab === 0">
                            <div v-if="!call.reports?.length" class="h-96 flex flex-col space-y-1 items-center justify-center p-4 pt-6">
                                <UIcon name="i-ph-file-x-duotone" class="size-5 text-gray-500" />
                                <span class="text-gray-500 text-[13px] italic">No reports.</span>
                            </div>
                            <div v-else class="space-y-4">
                                <CallReportCard 
                                    v-for="report in call.reports" 
                                    :key="report.id" 
                                    :report="report"
                                    :subscription="activeSubscription"
                                    :caller="call.caller"
                                    @refresh="fetchData(true)"
                                />
                            </div>
                        </template>
                        <template v-else-if="selectedTab === 1">
                            <div class="space-y-5">
                                <div class="grid grid-cols-3 gap-5 px-1">
                                    <Field label="City" class="col-span-1" truncate>
                                        <span v-if="call.address?.city">{{ call.address.city }}</span>
                                    </Field>
                                    <Field label="Region" class="col-span-1" truncate>
                                        <span v-if="call.address?.region">{{ call.address.region }}</span>
                                    </Field>
                                    <Field label="Country" class="col-span-1" truncate>
                                        <span v-if="call.address?.country">{{ call.address.country }}</span>
                                    </Field>
                                    <div class="col-span-3">
                                        <p class="text-xs uppercase leading-6 font-medium text-gray-500">Directions</p>
                                        <p class="text-gray-800">
                                            <span v-if="call.address?.directions">{{ call.address.directions }}</span>
                                            <span v-else class="text-gray-300">––</span>
                                        </p>
                                    </div>
                                </div>
                                <div v-if="call.address?.latitude && call.address?.longitude" class="w-full h-[480px] rounded-lg overflow-hidden">
                                    <Map
                                        controls 
                                        scroll-zoom-disabled 
                                        :center="{ 
                                            lat: call.address.latitude, 
                                            lng: call.address.longitude
                                        }"
                                    />
                                </div>
                            </div>
                        </template>
                        <template v-else-if="selectedTab === 2">
                            <div v-if="callRecordingLoading" class="flex items-center justify-center p-24">
                                <UIcon name="i-ph-circle-notch" class="size-5 text-gray-500 animate-spin-slow" />
                            </div>
                            <div v-else class="space-y-5">
                                <div v-if="callRecording?.id" class="grid grid-cols-3 gap-5 px-1">
                                    <Field label="Status" class="col-span-1" truncate>
                                        <UBadge v-if="callRecording?.processed_at" color="green" label="Processed" variant="subtle" />
                                        <UBadge v-else color="blue" label="Processing" variant="subtle" />
                                    </Field>
                                    <Field label="Duration" class="col-span-1" truncate>
                                        <span v-if="callRecording?.duration">
                                            {{ duration(dayjs().format(), dayjs().add(callRecording.duration, 'seconds').format()) }}
                                        </span>
                                    </Field>
                                    <Field label="Processed Date" class="col-span-1" truncate>
                                        <span v-if="callRecording?.processed_at">{{ callRecording.processed_at }}</span>
                                    </Field>
                                    <div class="col-span-3">
                                        <UButton v-if="callRecording.media" @click="viewRecording()" size="md">
                                            View Recording
                                        </UButton>
                                    </div>
                                </div>
                                <div v-else class="h-96 flex flex-col space-y-1 items-center justify-center p-4 pt-6">
                                    <UIcon name="i-ph-file-x-duotone" class="size-5 text-gray-500" />
                                    <span class="text-gray-500 text-[13px] italic">No recording.</span>
                                    <UButton @click="generateRecording()" size="md" class="!mt-4">
                                        Generate Recording
                                    </UButton>
                                </div>
                            </div>
                        </template>
                        <template v-else-if="selectedTab === 3">
                            <CallTimeline :events="call.events" :call="call" />
                        </template>
                    </div>
                </div>
            </div>
        </div>

        <OverlayCallCreateFeedbackReportModal v-model:open="overlays.createFeedbackReport" @reload="fetchData" :call="call" />
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import CallReportCard from '~/components/elements/CallReportCard.vue'
import CallTimeline from '~/components/call/CallTimeline.vue'
import Avatar from '~/components/global/Avatar.vue'
import type { Call, CallRecording } from '~/types/call'
import type { Customer } from '~/types/customer'
import { UserRole } from '~/types/user'

const route = useRoute()

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin, UserRole.medicalDirector, UserRole.doctor, UserRole.operator] })
useHead({ title: 'View Call' })

const auth = useAuthStore()
const client = useSupabaseClient()

const { age, duration } = useNumber()

const loading = ref(false)
const selectedTab = ref(0)
const call = ref<Call>()
const callRecording = ref<CallRecording>()
const callRecordingCheck = ref<NodeJS.Timeout>()
const callRecordingLoading = ref(false)

const overlays = reactive({
    createFeedbackReport: false,
})

const tabs = [
    { label: 'Reports', icon: 'i-ph-file-text-duotone' },
    { label: 'Location', icon: 'i-ph-map-pin-duotone' },
    { label: 'Recording', icon: 'i-ph-cassette-tape-duotone' },
    { label: 'Timeline', icon: 'i-ph-clock-counter-clockwise-duotone' },
]

const tabsUi = {
    wrapper: 'space-y-0 pt-1',
    container: 'border-b border-gray-300',
    list: {
        base: '!flex space-x-4 w-full overflow-x-auto px-1',
        background: 'bg-transparent',
        rounded: 'rounded-none',
        padding: 'p-0',
        height: 'h-auto',
        marker: {
            base: 'w-auto',
            wrapper: 'border-b-2 border-primary-600',
            background: 'bg-transparent',
            rounded: 'rounded-none',
            shadow: 'shadow-none',
        },
        tab: {
            base: 'h-auto w-auto',
            padding: 'px-3 py-3.5',
        },
    },
}

const callId = computed(() => {
    if (call.value) {
        return call.value.number.toString().padStart(8, '0')
    }
})

const customerName = computed(() => {
    if (call.value?.customer) {
        return [call.value.customer.first_name, call.value.customer.middle_name, call.value.customer.last_name].filter(Boolean).join(' ')
    }
})

const customerDemographics = computed(() => {
    if (call.value?.customer) {
        return [
            call.value.customer.gender, 
            age(call.value.customer.birth_date),
            call.value.customer.blood_type
        ].filter(Boolean).join(' · ')
    }
})

const activeSubscription = computed(() => {
    if (call.value?.customer?.subscription_id) {
        return call.value?.customer.subscriptions?.find((s) => s.id === call.value?.customer?.subscription_id)
    }
})

const hasDifferentCaller = computed(() => {
    if (!call.value?.caller_user_id) return false
    const customerUserId = (call.value.customer as Customer & { user_id?: string }).user_id
    return call.value.caller_user_id !== customerUserId
})

const callerName = computed(() => {
    if (call.value?.caller) {
        return [call.value.caller.first_name, call.value.caller.middle_name, call.value.caller.last_name].filter(Boolean).join(' ')
    }
})

const callerDemographics = computed(() => {
    if (call.value?.caller) {
        return [
            call.value.caller.gender,
            age(call.value.caller.birth_date)
        ].filter(Boolean).join(' · ')
    }
})

onMounted(async () => {
    await fetchData()
    await fetchRecording()
})

onUnmounted(() => {
    if (callRecordingCheck.value) {
        clearInterval(callRecordingCheck.value)
    }
})

async function fetchData(silent?: boolean) {
    if (!silent) {
        loading.value = true
    }

    const { data, error } = await client
        .from('calls')
        .select(`
            *,
            customer:customers (
                *,
                subscriptions (
                    id,
                    partner_id,
                    source_id,
                    source_type,
                    is_active,
                    expires_at,
                    partner:partners (id, name, avatar, metadata)
                )
            ),
            address:addresses (*),
            events:call_events (
                *,
                actor:users!call_events_actor_user_id_fkey (id, name),
                target:users!call_events_target_user_id_fkey (id, name)
            ),
            user:users!calls_user_id_fkey (id, name, role),
            reports:call_reports (
                *,
                customer:customers (id, phone, policy_holder),
                user:users (id, name, role),
                call_reason:options!call_reports_call_reason_id_fkey (id, label, description),
                call_decision:options!call_reports_call_decision_id_fkey (id, label, description),
                diagnosis:options!call_reports_diagnosis_id_fkey (id, label, description, metadata),
                documents:call_report_documents (*)
            )
        `)
        .eq('id', route.params.id)
        .order('number', { ascending: false, referencedTable: 'reports' })
        .order('created_at', { ascending: true, referencedTable: 'events' })
        .single()

    if (data) {
        const callData = data as unknown as Call & { caller_user_id?: string }
        call.value = callData

        const customerUserId = (callData.customer as Customer & { user_id?: string }).user_id
        if (callData.caller_user_id && callData.caller_user_id !== customerUserId) {
            const { data: callerData } = await client
                .from('customers')
                .select('id, first_name, middle_name, last_name, avatar, birth_date, gender, phone')
                .eq('user_id', callData.caller_user_id)
                .single()

            if (callerData) {
                call.value.caller = callerData as unknown as Customer
            }
        }
    } else if (error) {
        console.error(error)
    }

    loading.value = false
}

async function fetchRecording(silent?: boolean) {
    callRecordingLoading.value = !silent

    try {
        callRecording.value = await $fetch(`/api/calls/recording/${route.params.id}`)
        if (callRecording.value?.processed_at && callRecordingCheck.value) {
            clearInterval(callRecordingCheck.value)
        }
        else if (callRecording.value?.id && !callRecordingCheck.value) {
            callRecordingCheck.value = setInterval(() => fetchRecording(true), 5000)
        }
    } catch {} finally {
        callRecordingLoading.value = false
    }
}

async function generateRecording() {
    callRecordingLoading.value = true

    try {
        await $fetch('/api/calls/recording/' + route.params.id, { method: 'PUT' })
        await fetchRecording()
    } catch {}
}

function viewRecording() {
    if (callRecording.value?.media) {
        navigateTo(callRecording.value.media, { open: { target: '_blank' } })
    }
}
</script>
